import { useEffect, useRef, useState } from 'react';
import { trackExperiment } from './experiments';

/**
 * Hook to track scroll depth for A/B testing
 * @param {string} experimentName - Name of the experiment
 * @param {string} variant - Variant name (for logging)
 * @param {boolean} enabled - Whether tracking is enabled
 */
export function useScrollDepthTracking(experimentName, variant, enabled = true) {
  const trackedDepths = useRef(new Set());
  const [maxDepth, setMaxDepth] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Update max depth
      if (scrollPercentage > maxDepth) {
        setMaxDepth(scrollPercentage);
      }

      // Track milestones: 25%, 50%, 75%, 100%
      const milestones = [25, 50, 75, 100];
      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone);
          
          trackExperiment(experimentName, 'homepage_scrolled', {
            depth: `${milestone}%`,
            variant: variant
          });

          console.log(`[Scroll Depth] ${milestone}% reached on ${variant} variant`);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [experimentName, variant, maxDepth, enabled]);
}

/**
 * Hook to track CTA clicks for A/B testing
 * @param {string} experimentName - Name of the experiment
 * @param {string} variant - Variant name
 * @param {boolean} enabled - Whether tracking is enabled
 */
export function useCTATracking(experimentName, variant, enabled = true) {
  const trackCTAClick = (ctaType, additionalData = {}) => {
    if (!enabled) return;

    trackExperiment(experimentName, 'homepage_cta_clicked', {
      ctaType,
      variant,
      ...additionalData
    });

    console.log(`[CTA Click] ${ctaType} clicked on ${variant} variant`);
  };

  return { trackCTAClick };
}

/**
 * Higher-order component to add experiment tracking to a page
 * @param {React.Component} Component - Component to wrap
 * @param {string} experimentName - Name of the experiment
 * @param {string} variant - Variant name
 */
export function withExperimentTracking(Component, experimentName, variant) {
  return function TrackedComponent(props) {
    useScrollDepthTracking(experimentName, variant);
    const { trackCTAClick } = useCTATracking(experimentName, variant);

    return <Component {...props} trackCTAClick={trackCTAClick} />;
  };
}

export default {
  useScrollDepthTracking,
  useCTATracking,
  withExperimentTracking
};
